/* @flow */
import React from 'react';
import {FormattedMessage, injectIntl, type IntlShape} from 'react-intl';
import StarRating from '@homeaway/react-star-rating';
import {MoreOrLess} from '@homeaway/react-collapse';
import {Unveil} from '@homeaway/react-unveil';
import type {ListingShape} from '../../components/Hit/Hit';
import {Link} from '@homeaway/react-localization-components';
import gql from 'graphql-tag';
import {compose} from 'redux';

import './SearchReviews.less';

type IntlProps = {
    intl: IntlShape
};

type Review = {
    title: string,
    text: string,
    createdAt: string,
    arrivalDate: string,
    rating: number
};

export type SearchReviewSummary = {
    listingDetails: ListingShape,
    review: Review
};

type SearchReviewProps = Review & ListingShape & IntlProps;

type SearchReviewsProps = {
    reviews: SearchReviewSummary[]
} & IntlProps;

export const fragments = {
    TopRecentReviewSummary: gql`
        fragment SearchReviewsTopRecentReviewSummary on TopRecentReviewSummary {
            listingDetails {
                listingId
                thumbnailUrl
                detailPageUrl
            }
            review {
                title
                text
                createdAt
                arrivalDate
                rating
            }
        }
    `
};
export function SearchReview({
    thumbnailUrl,
    detailPageUrl,
    title,
    text,
    createdAt,
    arrivalDate,
    rating,
    intl
}: SearchReviewProps) {
    const createdAtFormatted = intl.formatDate(new Date(createdAt), {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });

    const arrivalDateFormatted = intl.formatDate(new Date(arrivalDate), {
        year: 'numeric',
        month: 'short'
    });

    // thumbnailUrl returned by graphql-traveler-property contains 'TREATMENT' substring instead of size
    // in discussion PDP explains that they need this field in this representation so they may choose needed size
    // we would not like this approach. They going to introduce new field to get thumbnail url: unitPhotos.headlinePhoto.uri,
    // implementation in process https://github.homeawaycorp.com/GraphQL/graphql-traveler-property/pull/209
    // Here temporary 'TREATMENT' is replaced with 'c3' till they implement new field and we will be able to use it.
    // TODO https://jira.homeawaycorp.com/browse/TRAVOPS-812
    const adjustedThumbnailUrl = (thumbnailUrl || '').replace(
        'TREATMENT',
        'c3'
    );

    return (
        <li className={'SearchReview'} data-wdio={'SearchReview'}>
            <div className={'SearchReview__image-section'}>
                <Link href={detailPageUrl}>
                    <div className={'SearchReview__image'}>
                        <Unveil threshold={400}>
                            {adjustedThumbnailUrl ? (
                                <img src={adjustedThumbnailUrl} alt={title} />
                            ) : (
                                <div className="empty-image" />
                            )}
                        </Unveil>
                    </div>
                </Link>
                <Link
                    className={'SearchReview__image-link'}
                    href={detailPageUrl}
                >
                    <FormattedMessage
                        defaultMessage={'View property'}
                        id={'searchReviewViewProperty'}
                    />
                </Link>
            </div>
            <div className={'SearchReview__info'}>
                <Link
                    className={'h4'}
                    href={detailPageUrl}
                    data-wdio={'SearchReview__headline'}
                >
                    {title}
                </Link>
                <div className={'SearchReview__info-item'}>
                    <StarRating rating={rating} />
                    <span className="SearchReview__rating-text">
                        <FormattedMessage
                            defaultMessage={`${rating} of 5`}
                            id="reviewRatingText"
                            values={{rating}}
                        />
                    </span>
                </div>
                <div
                    className={
                        'SearchReview__info-item SearchReview__more-or-less'
                    }
                >
                    <MoreOrLess
                        expanderPosition={'inline'}
                        lines={2}
                        moreText={
                            <FormattedMessage
                                defaultMessage={'more'}
                                id={'moreLabelLower'}
                            />
                        }
                    >
                        <p className={'SearchReview__review-text'}>{text}</p>
                    </MoreOrLess>
                </div>
                <div
                    className={
                        'SearchReview__info-item SearchReview__review-dates'
                    }
                >
                    <div className={'SearchReview__submitted-date'}>
                        <FormattedMessage
                            defaultMessage={'Submitted'}
                            id={'searchReviewSubmitted'}
                        />
                        {`: ${createdAtFormatted}`}
                    </div>
                    <div className={'SearchReview__arrival-date'}>
                        <FormattedMessage
                            defaultMessage={'Stayed'}
                            id={'searchReviewStayLabel'}
                        />
                        {`: ${arrivalDateFormatted}`}
                    </div>
                </div>
            </div>
        </li>
    );
}

function SearchReviews({reviews, intl}: SearchReviewsProps) {
    return (
        <ul className={'SearchReviews'} data-wdio={'SearchReviews'}>
            {reviews.map(({listingDetails, review}) => {
                return (
                    <SearchReview
                        {...listingDetails}
                        {...review}
                        intl={intl}
                        key={listingDetails.listingId}
                    />
                );
            })}
        </ul>
    );
}

export default compose(injectIntl)(SearchReviews);
